/**
 * one-stop/types — the renderer package's vocabulary.
 *
 * ONE DOCUMENT. ONE BLOCK RENDERER. TWO HOSTS.
 * The Journey Builder authors an experience document; this package turns that
 * document into pixels. The student portal SPA (`screens/portal`) and Studio's
 * Journey Builder preview both mount the SAME components over the SAME blocks —
 * what the admin sees is what the student gets, because it is one implementation,
 * not two kept in sync by discipline.
 *
 * THE COVERAGE INVARIANT
 * `ONE_STOP_EXPERIENCE_BLOCK_TYPES` is the closed set of One-Stop registry types
 * whose `CANVAS_BLOCK_CONSTRAINTS[type].allowed_document_kinds` includes
 * `'experience'`. `registry.tsx` types its map as
 * `satisfies Record<OneStopExperienceBlockType, BlockRenderer>`, so adding a type
 * to this union without adding a component is a COMPILE ERROR, not a blank
 * section discovered in production.
 *
 * THE UNION IS NOW TIED TO THE REGISTRY (2026-09-01), BUT IS STILL SPELLED HERE
 * The authority is `ONE_STOP_BLOCK_TYPES` / `CANVAS_BLOCK_CONSTRAINTS` in
 * `packages/api-contracts/src/canvas-one-stop-blocks.ts`, and that overlay IS now
 * applied — so the `satisfies readonly OneStopBlockType[]` below is live: a member
 * of this list that the registry does not declare, or one the registry renames or
 * retires, is a COMPILE ERROR here.
 *
 * WHY THE LIST IS NOT DERIVED OUTRIGHT, WHICH THE KIT EXPECTED TO BE ONE LINE.
 * The filter is `allowed_document_kinds` including `'experience'`, and
 * `CANVAS_BLOCK_CONSTRAINTS` is built at RUNTIME (`Object.fromEntries` over the
 * type list, typed `Record<CanvasBlockTypeName, CanvasBlockConstraints>`), so the
 * per-type document kinds do not survive into the type system and no `Extract<…>`
 * can read them. Spelling the list and constraining it is the strongest tie the
 * contract's own shape allows.
 *
 * The other half of the invariant is therefore a runtime one, and it lives in
 * `registry.tsx`: at module load, the set below is compared against the types the
 * constraint table actually admits into an `experience` document, and a mismatch
 * in either direction is reported. Between the two, drift is caught at compile
 * time when it can be and at first render when it cannot.
 */
import type { ReactNode } from "react";
import type { OneStopBlockType, PortalAnonymousBlock, PortalSessionBlock } from "@v0y4ge/api-contracts";

/**
 * A block as either flavor of the read family delivers it. The anonymous flavor
 * cannot carry session-bearing fragments (the contract refuses them); the
 * session flavor can. The renderers narrow `data` by `kind` and never assume.
 */
export type OneStopBlock = PortalAnonymousBlock | PortalSessionBlock;

/**
 * The 20 One-Stop registry types allowed in an `experience` document, in the
 * docket's family order: 9 live, 3 flow-entry, 8 content.
 *
 * NOT in this list, both deliberately:
 *  - the `step` family (`service-picker`, `time-slot-picker`, `personal-details`,
 *    `consent`, `confirmation`, …) — `allowed_document_kinds: ['journey']`. A
 *    publish that places one in an experience document is refused with
 *    `block-kind-not-allowed`. Excluded by construction, not forgotten.
 *  - the 45 shipped canvas types (`text`, `image`, `hero_banner`, `countdown`,
 *    `video_embed`, `email_*`, `root_page`, …). They are authorable in an
 *    experience document TODAY by omission — which is why the unsupported path in
 *    `registry.tsx` is a real code path, not a theoretical one. They are the
 *    SHARED block library, owned by JAI-1157, and are never re-implemented here.
 */
export const ONE_STOP_EXPERIENCE_BLOCK_TYPES = [
  // live — resolved server-side, may carry a `data` fragment
  "search",
  "identity-panel",
  "context-banner",
  "queues",
  "appointments",
  "events",
  "directory",
  "checklist",
  "my-visits",
  // flow-entry — config only; each opens one of the platform's public flows
  "book-appointment",
  "register-event",
  "join-queue",
  // content — config only
  "help-contact",
  "notice",
  "button",
  "faq",
  "quote",
  "stats",
  "social",
  "spacer",
  /* Promoted from ONE_STOP_PENDING_BLOCK_TYPES: the contract ships it, and as of
     kit-full-2026-09-09-4ad74e1f BOTH halves agree — `ONE_STOP_BLOCK_TYPES` in
     src/canvas-one-stop-blocks.ts and dist/canvas-one-stop-blocks.js carry
     'featured-event', and both src/canvas.ts and dist/canvas.js give it a
     CANVAS_BLOCK_CONSTRAINTS entry (singleton, 'live' surface). See the note
     below for the reading error that briefly un-promoted it. */
  "featured-event",
] as const satisfies readonly OneStopBlockType[];

/**
 * PENDING CONTRACT TYPES — rendered here BEFORE the contract admits them.
 *
 * EMPTY, and empty is the correct state today.
 *
 * A type goes here when this package has the renderer arm and the contract does
 * NOT yet carry the type. It is kept out of the `satisfies readonly
 * OneStopBlockType[]` list so this package still compiles against the kit it
 * ships with, while the union and the runtime set below still force a renderer
 * arm to exist.
 *
 * ⚠️ THE READING ERROR THAT COST A DAY (2026-09-08), AND WHY IT CANNOT RECUR.
 * `featured-event` was promoted, reverted here on a bad reading, and promoted
 * again within a day. The bad reading was searching
 * `packages/api-contracts/src/` and concluding the contract did not carry the
 * type. That directory WAS a partial vendored copy — the 08-24 kit was a delta
 * pack (changed sources plus backfill), so `src/` was a subset while `dist/` was
 * whole, and the preview announced it on every load ("barrel-patching N missing
 * contract exports (partial kit src)").
 *
 * As of kit-full-2026-09-09-4ad74e1f that asymmetry is gone: the kit is a FULL
 * pack, every package ships its whole `src/` beside `dist/`, and the
 * barrel-patching notice should not appear. Either directory now answers the
 * question. The durable rule is the smaller one: the app resolves from `dist/`,
 * so `dist/` is what decides, and a disagreement between the two is a kit bug to
 * report rather than a judgement call to make here.
 *
 * The runtime guard in registry.tsx is the arbiter, and note its asymmetry,
 * which is what made the wrong revert look plausible for a moment:
 *
 *     missing = admitted − rendered            ← does NOT subtract pending
 *     extra   = rendered − admitted − pending
 *
 * So parking a type in `pending` silences `extra` and CANNOT silence `missing`.
 * If the console says "admitted … but not rendered here", the answer is always to
 * render it — putting it back in this list is not an option, it is the other
 * error.
 */
export const ONE_STOP_PENDING_BLOCK_TYPES = [] as const satisfies readonly string[];

export type OneStopPendingBlockType = (typeof ONE_STOP_PENDING_BLOCK_TYPES)[number];

export type OneStopExperienceBlockType =
  | (typeof ONE_STOP_EXPERIENCE_BLOCK_TYPES)[number]
  | OneStopPendingBlockType;

const EXPERIENCE_TYPE_SET: ReadonlySet<string> = new Set([
  ...ONE_STOP_EXPERIENCE_BLOCK_TYPES,
  ...ONE_STOP_PENDING_BLOCK_TYPES,
]);

/** Narrowing guard — the ONLY place a `block.type` string becomes a known type. */
export function isOneStopExperienceBlockType(type: string): type is OneStopExperienceBlockType {
  return EXPERIENCE_TYPE_SET.has(type);
}

/**
 * Which host is rendering. The ONLY behavioural difference between the two:
 * an unrecognised block type renders a visible "Unsupported block" card in the
 * builder (so an admin learns before publishing) and NOTHING on the student page
 * (so a published document outliving this client degrades quietly).
 */
export type BlockHost = "portal" | "builder";

/**
 * The `search` block's state, owned by the HOST and handed to the block.
 *
 * The block owns the CARD (the prototype's white `radius-md` panel, its input row
 * and its chip row); the host owns the query, the request and the results —
 * because the results replace part of the page's own block list, which is a page
 * concern. The builder host passes `null`: the same card renders, inert, so an
 * admin sees the real component and cannot fire a live query from a preview.
 *
 * Milestone 1 passed a pre-rendered `searchSlot: ReactNode` instead, which put
 * the search card's LOOK in the portal screen — unreachable from Studio, and
 * therefore the one block the two hosts could not have rendered identically.
 */
export interface SearchController {
  readonly value: string;
  readonly status: "idle" | "searching" | "error" | "done";
  readonly errorMessage: string | null;
  onChange(value: string): void;
  onSubmit(query: string): void;
  onClear(): void;
}

/**
 * THE PAGE'S HOLD GATE, as the renderer needs it (JAI-1766).
 *
 * Assembled by the HOST from two places the wire already carries separately —
 * `gate` on the session document (which categories narrow this page, and which
 * of them nothing here can clear) and `holdsGate` + the per-hold copy on the
 * identity fragment (the words, already resolved tenant-over-platform in the
 * document's locale). The renderer composes; it never re-derives either.
 *
 * `null` is the ordinary case and means NO GATE: an anonymous page, a student
 * with no active holds, and a degraded SIS context with nothing known all land
 * here. See `portal-hold-gate.ts` — degrade never blocks.
 */
export interface HoldGateView {
  /** The gating categories, platform order. Never includes `unknown`. */
  readonly categories: readonly string[];
  /**
   * Those gating categories nothing live on this tenant declares it resolves.
   * EMPTY on a partial door outage, deliberately — the port reports
   * completeness rather than inferring it, so "we could not check" never
   * renders as "there is nothing".
   */
  readonly unresolved: readonly string[];
  /** Tenant-or-platform sentence shown above the narrowed catalog. */
  readonly noticeText: string;
  /** Tenant-or-platform sentence for a category with nothing to clear it. */
  readonly unresolvedText: string;
  /**
   * Student-facing titles for `unresolved`, in the same order — resolved by the
   * server through tenant copy → platform default → source label → slug. The
   * renderer never prints a raw category slug.
   */
  readonly unresolvedTitles: readonly string[];
}

export interface BlockContext {
  /** The locale the document was RESOLVED in — also the config locale-map key. */
  readonly locale: string;
  /** Tenant display zone, for civil times that carry no zone of their own. */
  readonly timeZone: string;
  /** Portal page vs. Journey Builder preview. */
  readonly host: BlockHost;
  /** The live search state, or `null` for an inert render. */
  readonly search: SearchController | null;
  /**
   * The hold gate narrowing this page, or `null`/absent when nothing narrows it.
   * OPTIONAL because only the signed-in portal host can know it: the anonymous
   * read carries no gate and the Journey Builder preview has no student.
   */
  readonly holdGate?: HoldGateView | null;
}

export interface BlockRenderProps {
  readonly block: OneStopBlock;
  readonly ctx: BlockContext;
}

/** One component per registry type. Returning `null` is a legitimate render. */
export type BlockRenderer = (props: BlockRenderProps) => ReactNode;
